import React from 'react';
import { Table, Button, Badge, Dropdown, Spinner } from 'react-bootstrap';
import { formatDateToYYYYMMDD } from '../../../../utils/dateUtils';
import { CronJobStatus } from '../../../../models/CronJob';
import type { CronJobListProps } from './types';
import { getCronJobStatusColor, getCronJobStatusText } from './types';

// Helper function to determine if a string is ISO datetime format
const isISOString = (str: string): boolean => {
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{3})?)?Z?$/;
  return isoRegex.test(str);
};

const CronJobList: React.FC<CronJobListProps> = ({
  cronJobs,
  onView,
  onEdit,
  onDelete,
  onStatusChange,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <div className="mt-2">Loading cron jobs...</div>
      </div>
    );
  }

  if (cronJobs.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">No cron jobs found</p>
      </div>
    );
  }

  const getStatusActions = (cronJob: any) => {
    const actions = [];

    if (cronJob.status === CronJobStatus.DISABLED) {
      actions.push(
        <Dropdown.Item key="enable" onClick={() => onStatusChange(cronJob, CronJobStatus.ENABLED)}>
          Enable
        </Dropdown.Item>,
      );
    }

    if (cronJob.status === CronJobStatus.ENABLED) {
      actions.push(
        <Dropdown.Item key="disable" onClick={() => onStatusChange(cronJob, CronJobStatus.DISABLED)}>
          Disable
        </Dropdown.Item>,
      );
    }

    return actions;
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'primary';
      case 'POST':
        return 'success';
      case 'PUT':
        return 'warning';
      case 'DELETE':
        return 'danger';
      case 'PATCH':
        return 'info';
      default:
        return 'secondary';
    }
  };

  return (
    <Table responsive bordered hover className="align-middle">
      <thead className="bg-light">
        <tr>
          <th>ID</th>
          <th>Name</th>
          <th>Schedule</th>
          <th>HTTP Method</th>
          <th>URL</th>
          <th>Status</th>
          <th>Created</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {cronJobs.map((cronJob) => (
          <tr key={cronJob.id}>
            <td>
              <span className="badge bg-primary">{cronJob.id}</span>
            </td>
            <td>
              <strong>{cronJob.name}</strong>
            </td>
            <td>
              {isISOString(cronJob.cronExpression) ? (
                <div>
                  <Badge bg="info" className="me-2">
                    One Time
                  </Badge>
                  <span className="small">{cronJob.cronExpression}</span>
                </div>
              ) : (
                <div>
                  <Badge bg="secondary" className="me-2">
                    Recurring
                  </Badge>
                  <code>{cronJob.cronExpression}</code>
                </div>
              )}
            </td>
            <td>
              <Badge bg={getMethodColor(cronJob.command.argument?.method || 'GET')}>
                {cronJob.command.argument?.method || 'N/A'}
              </Badge>
            </td>
            <td>
              <div style={{ maxWidth: '200px', wordBreak: 'break-all' }}>
                {(cronJob.command.argument as any)?.url || '-'}
              </div>
            </td>
            <td>
              <Badge bg={getCronJobStatusColor(cronJob.status)}>{getCronJobStatusText(cronJob.status)}</Badge>
            </td>
            <td>{cronJob.createdAt ? formatDateToYYYYMMDD(cronJob.createdAt) : '-'}</td>
            <td>
              <div className="d-flex gap-2">
                <Button variant="outline-info" size="sm" onClick={() => onView(cronJob)}>
                  View
                </Button>
                <Button variant="outline-primary" size="sm" onClick={() => onEdit(cronJob)}>
                  Edit
                </Button>

                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" size="sm">
                    Status
                  </Dropdown.Toggle>
                  <Dropdown.Menu>{getStatusActions(cronJob)}</Dropdown.Menu>
                </Dropdown>
                <Button variant="outline-danger" size="sm" onClick={() => onDelete(cronJob)}>
                  Delete
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default CronJobList;
